/**
 * «Персонажи»: сетка героинь. Героиня — постоянное лицо наших рилсов; её портрет и
 * лист (канон) потом подставляются в кадры. Создание — одно поле «имя», остальное
 * делается уже на экране героини.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, UserRound } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { Dialog, DialogFooter } from "@/components/trends/Dialog";
import { btn, btnPrimary, Empty, ErrorLine, input, Loading, Panel } from "@/components/trends/ui";
import { mediaUrl, num } from "@/lib/media";
import { createPersona, loadPersonas, personaKeys, type PersonaCard } from "@/lib/persona";
import { usd } from "@/lib/project";
import { retryQuery } from "@/lib/trends";

export function PersonasPage() {
  const [creating, setCreating] = useState(false);
  const list = useQuery({ queryKey: personaKeys.list, queryFn: loadPersonas, retry: retryQuery });

  if (list.isPending) return <Loading />;
  if (list.isError)
    return (
      <div className="p-5">
        <ErrorLine error={list.error} />
      </div>
    );

  const { personas } = list.data;

  return (
    <div className="grid gap-4 p-5">
      <div className="flex items-start gap-3">
        <div>
          <h1 className="text-[18px] font-semibold tracking-[-0.01em] text-fg">Персонажи</h1>
          <p className="mt-0.5 text-[12.5px] text-fg-muted">
            Героини наших рилсов: портрет и лист образа, которые потом подставляются в кадры.
          </p>
        </div>
        <button type="button" className={`${btnPrimary} ml-auto`} onClick={() => setCreating(true)}>
          <Plus size={13} />
          Новая героиня
        </button>
      </div>

      {personas.length === 0 ? (
        <Panel annot="героини">
          <Empty>
            <span className="grid justify-items-center gap-2">
              Героинь пока нет.
              <button type="button" className={btn} onClick={() => setCreating(true)}>
                <Plus size={13} />
                Новая героиня
              </button>
            </span>
          </Empty>
        </Panel>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-3">
          {personas.map((p) => (
            <PersonaTile key={p.id} persona={p} />
          ))}
        </div>
      )}

      <CreateDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function PersonaTile({ persona }: { persona: PersonaCard }) {
  const approved = persona.status === "approved";
  return (
    <Link
      to={`/persona/${persona.id}`}
      className="group grid overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-subtle transition-colors hover:border-border-strong"
      data-annot="героиня"
    >
      <div className="aspect-[3/4] overflow-hidden bg-bg-elevated">
        {persona.cover_path ? (
          <img
            src={mediaUrl(persona.cover_path)}
            alt={persona.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="grid h-full place-items-center text-fg-faint">
            <UserRound size={32} />
          </div>
        )}
      </div>
      <div className="grid gap-1 px-2.5 py-2">
        <div className="flex items-center gap-2">
          <div className="min-w-0 truncate text-[13px] font-medium text-fg group-hover:text-accent-text" title={persona.name}>
            {persona.name}
          </div>
          <span
            className={
              "ml-auto flex-none rounded px-1.5 py-0.5 text-[10.5px] " +
              (approved ? "bg-ok-bg text-ok" : "bg-warn-bg text-warn")
            }
          >
            {approved ? "Утверждена" : "Черновик"}
          </span>
        </div>
        <div className="text-[11.5px] text-fg-muted tnum">
          {num(persona.images)} картинок · {usd(persona.spent)}
        </div>
      </div>
    </Link>
  );
}

function CreateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState("");

  const create = useMutation({
    mutationFn: () => createPersona(name.trim()),
    onSuccess: ({ id }) => {
      void qc.invalidateQueries({ queryKey: personaKeys.list });
      setName("");
      onClose();
      navigate(`/persona/${id}`);
    },
  });

  const submit = () => {
    if (name.trim() && !create.isPending) create.mutate();
  };

  return (
    <Dialog open={open} title="Новая героиня" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="grid gap-2 p-4">
          <label className="grid gap-1 text-[11.5px] text-fg-faint">
            Имя
            <input
              className={`${input} w-full`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              autoFocus
            />
          </label>
          <ErrorLine error={create.error} />
        </div>
        <DialogFooter>
          <button type="button" className={btn} onClick={onClose}>
            Отмена
          </button>
          <button type="submit" className={btnPrimary} disabled={!name.trim() || create.isPending}>
            {create.isPending ? "Создаю…" : "Создать"}
          </button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
